/**
 * Adjusts a position to keep an element within the viewport boundaries and editor boundary
 * @param {Object} position - Position object {x, y}
 * @param {DOMRect} elementRect - The element's bounding rectangle
 * @param {Object} options - Configuration options
 * @param {number} options.margin - Margin from viewport edges (default: 10)
 * @param {boolean} options.allowFlip - Whether to flip position if element goes off screen (default: true)
 * @param {boolean} options.preferLeft - Whether to prefer left-side positioning (default: false)
 * @param {Object} options.editorBoundary - Editor boundary { top, bottom } to constrain within
 * @returns {Object} - Adjusted position {x, y}
 */
export function adjustPositionToViewport(position, elementRect, options = {}) {
  const { margin = 10, allowFlip = true, preferLeft = false, editorBoundary } = options;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let { x, y } = position;

  // If preferLeft, ensure it doesn't go off left edge
  if (preferLeft) {
    if (x < margin) {
      x = margin;
    }
    // Allow it to extend off right since we want left positioning
  } else {
    // Original right edge handling
    if (x + elementRect.width > viewportWidth - margin) {
      x = viewportWidth - elementRect.width - margin;
    }
    if (x < margin) {
      x = margin;
    }
  }

  // Adjust if element goes off bottom edge
  if (y + elementRect.height > viewportHeight - margin) {
    if (allowFlip) {
      // Try to flip above the original position
      y = position.y - elementRect.height - margin;
      // If still off screen, use the bottom edge adjustment
      if (y < margin) {
        y = viewportHeight - elementRect.height - margin;
      }
    } else {
      y = viewportHeight - elementRect.height - margin;
    }
  }

  // Adjust if element goes off top edge
  if (y < margin) {
    y = margin;
  }

  // Apply editor boundary constraints if provided
  if (editorBoundary) {
    const constrained = constrainToBoundary({ x, y }, elementRect, editorBoundary, { margin });
    x = constrained.x;
    y = constrained.y;
  }

  return { x, y };
}

/**
 * Gets the boundary coordinates of the .ProseMirror editor element
 * @returns {Object|null} - Boundary object { top, bottom } or null if element not found
 */
export function getEditorBoundary() {
  const editor = document.querySelector('.ProseMirror');
  if (!editor) return null;

  const rect = editor.getBoundingClientRect();
  return {
    top: rect.top,
    bottom: rect.bottom
  };
}

/**
 * Constrains a position to stay within a given boundary
 * @param {Object} position - Position object {x, y}
 * @param {DOMRect} elementRect - The element's bounding rectangle
 * @param {Object} boundary - Boundary object { top, bottom }
 * @param {Object} options - Configuration options
 * @param {number} options.margin - Margin from boundary edges (default: 10)
 * @returns {Object} - Constrained position {x, y}
 */
export function constrainToBoundary(position, elementRect, boundary, options = {}) {
  if (!boundary) return position;

  const { margin = 10 } = options;

  let { x, y } = position;

  // Constrain Y position to stay within boundary
  if (y < boundary.top + margin) {
    y = boundary.top + margin;
  }

  if (y + elementRect.height > boundary.bottom - margin) {
    y = boundary.bottom - elementRect.height - margin;
  }

  return { x, y };
}

